import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData } from "@/lib/api-response";
import { downloadAuthenticatedFile } from "@/lib/download-blob";
import type {
  ContentReport,
  ContentReportQuery,
  ExportReportQuery,
  Ga4Config,
  Ga4Report,
  ReportScope,
  UserActivityAuditQuery,
  UserActivityLoginsQuery,
  UsersReport,
  UsersReportQuery,
} from "@/types/report";
import type { AuditLog } from "@/types/audit-log";
import type { User } from "@/types/user";

export interface ReportPagination {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

export interface UserActivityLoginsResult {
  items: User[];
  pagination: ReportPagination;
}

export interface UserActivityAuditResult {
  items: AuditLog[];
  pagination: ReportPagination;
}

const EMPTY_PAGINATION: ReportPagination = { page: 1, pageSize: 0, total: 0, pageCount: 1 };

export const reportService = {
  async getScope(): Promise<ReportScope> {
    const response = await http.get(API_ENDPOINTS.REPORTS.SCOPE);
    return unwrapData<ReportScope>(response);
  },

  async contentReport(query?: ContentReportQuery): Promise<ContentReport> {
    const response = await http.get(API_ENDPOINTS.REPORTS.CONTENT, {
      params: query,
    });
    return unwrapData<ContentReport>(response);
  },

  // The backend streams a real CSV of the by-department breakdown when format=csv,
  // honoring the same date range as the on-screen report.
  async exportContentReport(query?: ContentReportQuery): Promise<void> {
    const format = query?.format ?? "csv";
    const ext = format === "xlsx" ? "xlsx" : "csv";
    await downloadAuthenticatedFile(
      API_ENDPOINTS.REPORTS.CONTENT,
      { ...query, format },
      `content-report.${ext}`,
    );
  },

  async usersReport(query?: UsersReportQuery): Promise<UsersReport> {
    const response = await http.get(API_ENDPOINTS.REPORTS.USERS, {
      params: query,
    });
    return unwrapData<UsersReport>(response);
  },

  async exportUsersReport(
    query?: UsersReportQuery & { format?: "csv" | "xlsx" },
  ): Promise<void> {
    const format = query?.format ?? "csv";
    const ext = format === "xlsx" ? "xlsx" : "csv";
    await downloadAuthenticatedFile(
      API_ENDPOINTS.REPORTS.USERS,
      { ...query, format },
      `users-report.${ext}`,
    );
  },

  async userActivityLogins(
    query?: UserActivityLoginsQuery,
  ): Promise<UserActivityLoginsResult> {
    const response = await http.get(API_ENDPOINTS.REPORTS.USER_ACTIVITY_LOGINS, {
      params: query,
    });
    const data = unwrapData<{ items?: User[]; pagination?: ReportPagination }>(
      response,
    );
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      pagination: data?.pagination ?? EMPTY_PAGINATION,
    };
  },

  async exportUserActivityLogins(
    query?: UserActivityLoginsQuery,
  ): Promise<void> {
    const format = query?.format ?? "csv";
    const ext = format === "xlsx" ? "xlsx" : "csv";
    await downloadAuthenticatedFile(
      API_ENDPOINTS.REPORTS.USER_ACTIVITY_LOGINS,
      { ...query, format },
      `user-logins.${ext}`,
    );
  },

  async userActivityAudit(
    query?: UserActivityAuditQuery,
  ): Promise<UserActivityAuditResult> {
    const response = await http.get(API_ENDPOINTS.REPORTS.USER_ACTIVITY_AUDIT, {
      params: {
        ...query,
        contentOnly: query?.contentOnly ? "true" : undefined,
      },
    });
    const data = unwrapData<{ items?: AuditLog[]; pagination?: ReportPagination }>(
      response,
    );
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      pagination: data?.pagination ?? EMPTY_PAGINATION,
    };
  },

  async export(query: ExportReportQuery): Promise<void> {
    const ext = query.format === "xlsx" ? "xlsx" : "csv";
    await downloadAuthenticatedFile(
      API_ENDPOINTS.REPORTS.EXPORT,
      query,
      `report-${query.reportType}.${ext}`,
    );
  },

  async websiteAnalytics(query?: {
    from?: string;
    to?: string;
  }): Promise<Ga4Report> {
    const response = await http.get(API_ENDPOINTS.REPORTS.WEBSITE_ANALYTICS, {
      params: query,
    });
    return unwrapData<Ga4Report>(response);
  },

  async getGa4Config(): Promise<Ga4Config> {
    const response = await http.get(API_ENDPOINTS.REPORTS.WEBSITE_ANALYTICS_CONFIG);
    return unwrapData<Ga4Config>(response);
  },

  async updateGa4Config(propertyId?: string): Promise<Ga4Config> {
    const response = await http.put(API_ENDPOINTS.REPORTS.WEBSITE_ANALYTICS_CONFIG, {
      propertyId,
    });
    return unwrapData<Ga4Config>(response);
  },
};
